<form [formGroup]="form">
  <h3 class="mb-2">Give your agreement a name</h3>
  <div class="mb-2 max-w-[400px]">
    <mat-form-field class="fw">
      <input matInput type="text" class="mb-4" placeholder="Agreement" formControlName="name" [required]="true" />
      <mat-error>Name is required</mat-error>
    </mat-form-field>
  </div>

  <h3 class="mb-2">Who receives reservation payouts?</h3>
  <div class="flex flex-col md:flex-row gap-6 mb-8 items-stretch">
    <sbnb-radio-card
      [disabled]="!form.disabled && form.get('type').disabled"
      [readonly]="form.disabled"
      context="neutral"
      *ngFor="let option of payoutsOptions"
      [selected]="form.get('type').value === option.value"
      (click)="form.get('type').setValue(option.value)">
      <h4 class="mb-2">{{ option.title }}</h4>
      <p>
        {{ option.subtitle }}
      </p>
    </sbnb-radio-card>
  </div>

  <div>
    <h3>Configure your earnings</h3>
    <p>
      Earnings are determined per reservation, and can combine percentage-based commission, guest fees split between you
      and your owner, flat fees charged to the owner and pass-through taxes.
    </p>
  </div>

  <div class="flex flex-col gap-6">
    <!-- Commission -->
    <div class="form-group flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div>
          <h3>Commission</h3>
          <div class="text-night-lighter text-sm">Percentage-based commission on your reservation revenue.</div>
        </div>
      </div>
      <div>
        <div class="flex flex-col gap-3">
          <strong class="flex-1">Manager percentage</strong>

          <div class="flex items-center gap-2" formGroupName="ui">
            <mat-form-field
              *ngIf="!form.get('ui.commissionRatesPerPlatform').value"
              class="w-24 zeroSuffix nopadding !text-right">
              <input matInput type="number" placeholder="25" formControlName="commissionRatesDefault" />
              <div matSuffix>%</div>
            </mat-form-field>

            <mat-checkbox [checked]="false" formControlName="commissionRatesPerPlatform">
              <div [ngClass]="{ 'text-small': !form.get('ui.commissionRatesPerPlatform').value }">
                Set rates per platform
              </div>
            </mat-checkbox>
          </div>
        </div>

        <div
          formGroupName="commission_rates"
          *ngIf="form.get('ui.commissionRatesPerPlatform').value"
          class="grid grid-cols-[repeat(auto-fit,minmax(88px,1fr))] gap-4">
          <div class="w-[5rem] flex flex-col gap-1" *ngFor="let field of perPlatformOptions">
            <label style="font-size: 0.75rem">{{ field.label }}</label>
            <mat-form-field class="input__mini nopadding zeroSuffix w-[75px] !text-right">
              <input matInput type="number" placeholder="25" [formControlName]="field.value" max="100" min="0" />
              <div matSuffix>%</div>
            </mat-form-field>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-3">
        <strong class="flex-1">What do you charge commission from?</strong>

        <div class="flex flex-col items-center gap-2">
          <sbnb-radio-card
            [disabled]="!form.disabled && form.get('ui.commissionAccounts').disabled"
            [readonly]="form.disabled"
            context="neutral"
            class="w-full"
            *ngFor="let source of commissionAccountOptions"
            [selected]="form.get('ui.commissionAccounts').value === source.value"
            (click)="form.get('ui.commissionAccounts').setValue(source.value)">
            <h4>{{ source.title }}</h4>
            <span class="text-small">{{ source.subtitle }}</span>

            <mat-form-field
              *ngIf="source.value === 'custom' && form.get('ui.commissionAccounts').value === source.value"
              class="w-full mt-4 nopadding bg-white">
              <mat-select
                [multiple]="true"
                placeholder="Select fees"
                formControlName="commission_accounts"
                [compareWith]="compareCommissionAccountSelection">
                <mat-option *ngFor="let fee of commissionAccountCustomOptions" [value]="fee.value">
                  {{ fee.label }}
                </mat-option>
              </mat-select>
            </mat-form-field>
          </sbnb-radio-card>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <strong class="flex-1">How do you want to recognize your commission revenue?</strong>

        <div class="flex flex-col items-center gap-2">
          <sbnb-radio-card
            [disabled]="!form.disabled && form.get('commission_recognition').disabled"
            [readonly]="form.disabled"
            context="neutral"
            class="w-full"
            *ngFor="let source of recognitionOptions"
            [selected]="form.get('commission_recognition').value === source.value"
            (click)="form.get('commission_recognition').setValue(source.value)">
            <h4>{{ source.title }}</h4>
            <span class="text-small">{{ source.subtitle }}</span>
          </sbnb-radio-card>
        </div>
      </div>
    </div>

    <!-- Splits -->
    <div class="form-group flex flex-col gap-4" [class.muted]="form.get('ui.commissionAccounts').value === 'payout'">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3>Reservation fees</h3>
          <div class="text-night-lighter text-sm">
            Reservation fees include guest fees and host fees on the reservation. You can keep all fees, give them to
            the Owner or split them between you.
          </div>
          <sbnb-alert-card *ngIf="form.get('ui.commissionAccounts').value === 'payout'" class="mt-3">
            <div class="text-night strong">
              Since commission is charged from payout, all fees are included in the payout and are not customizable.
            </div>
          </sbnb-alert-card>
        </div>
      </div>
      <ng-container *ngIf="form.get('ui.commissionAccounts').value !== 'payout'">
        <div class="flex flex-col gap-3">
          <div class="flex flex-col items-center gap-2">
            <sbnb-radio-card
              [disabled]="
                !form.disabled && form.get('ui.splits').disabled && form.get('ui.splits').value !== splitOption.value
              "
              [readonly]="form.disabled"
              context="neutral"
              class="w-full"
              *ngFor="let splitOption of splitOptions"
              [selected]="form.get('ui.splits').value === splitOption.value"
              (click)="form.get('ui.splits').setValue(splitOption.value)">
              <h4>{{ splitOption.title }}</h4>
              <span class="text-small">{{ splitOption.subtitle }}</span>

              <div
                formGroupName="splits"
                *ngIf="splitOption.value === 'custom' && form.get('ui.splits').value === splitOption.value">
                <sbnb-input-with-slider
                  *ngFor="let account of commissionAccountCustomOptions"
                  [disabled]="form.get('splits.' + account.value).disabled"
                  [label]="account.label"
                  [controlName]="account.value"
                  [fieldValue]="form.get('splits.' + account.value)?.value"></sbnb-input-with-slider>
              </div>
            </sbnb-radio-card>
          </div>
        </div>

        <div class="flex flex-col gap-3">
          <strong class="flex-1">How do you want to recognize revenue from fees?</strong>

          <div class="flex flex-col items-center gap-2">
            <sbnb-radio-card
              [disabled]="!form.disabled && form.get('ui.recognition').disabled"
              [readonly]="form.disabled"
              context="neutral"
              class="w-full"
              *ngFor="let source of splitRecognitionOptions"
              [selected]="form.get('ui.recognition').value === source.value"
              (click)="form.get('ui.recognition').setValue(source.value)">
              <h4>{{ source.title }}</h4>
              <span class="text-small">{{ source.subtitle }}</span>
            </sbnb-radio-card>
            <sbnb-radio-card
              [disabled]="!form.disabled && form.get('ui.recognition').disabled"
              [readonly]="form.disabled"
              context="neutral"
              class="w-full"
              [selected]="form.get('ui.recognition').value === 'custom'"
              (click)="form.get('ui.recognition').setValue('custom')">
              <h4>Custom</h4>
              <span class="text-small">
                Define how you recognize revenue for each individual fee on the reservation.
              </span>
              <div
                formGroupName="recognition"
                *ngIf="form.get('ui.recognition').value === 'custom'"
                class="grid grid-cols-[200px_1fr] items-center mt-4">
                <ng-container *ngFor="let account of commissionAccountCustomOptions">
                  <label
                    class="max-w-[200px]"
                    [ngClass]="{ 'opacity-50': form.get('recognition.' + account.value).disabled }"
                    [id]="account.value">
                    {{ account.label }}
                  </label>
                  <mat-radio-group
                    [formControlName]="account.value"
                    aria-labelledby="{{ account.value }}"
                    class="flex justify-between">
                    <mat-radio-button value="pro-rated">Pro-rated</mat-radio-button>
                    <mat-radio-button value="check-in">Check-in</mat-radio-button>
                    <mat-radio-button value="check-out">Checkout</mat-radio-button>
                  </mat-radio-group>
                </ng-container>
              </div>
            </sbnb-radio-card>
          </div>
        </div>
      </ng-container>
    </div>

    <!-- Flat fees -->
    <div class="form-group flex flex-col gap-4" [class.muted]="!form.get('ui.flatFeesShow').value">
      <div class="flex items-center justify-between">
        <div class="flex-1">
          <h3>Flat fee per reservation</h3>
          <div class="text-night-lighter text-sm">A flat fee charged to the owner per reservation.</div>
        </div>
        <sbnb-button
          *ngIf="!form.disabled"
          size="mini"
          type="secondary"
          (click)="form.get('ui.flatFeesShow').setValue(!form.get('ui.flatFeesShow').value)">
          {{ form.get('ui.flatFeesShow').value ? 'Exclude' : 'Include' }} flat fee
        </sbnb-button>
      </div>
      <ng-container *ngIf="form.get('ui.flatFeesShow').value">
        <div>
          <div class="flex flex-col gap-3">
            <strong class="flex-1">Fee</strong>

            <div class="flex items-center gap-2" formGroupName="ui">
              <mat-form-field
                *ngIf="!form.get('ui.flatFeesPerPlatform').value"
                class="w-24 zeroSuffix nopadding !text-right">
                <input matInput type="number" placeholder="0" min="0" formControlName="flatFeesDefault" />
              </mat-form-field>

              <mat-checkbox formControlName="flatFeesPerPlatform">
                <div [ngClass]="{ 'text-small': !form.get('ui.flatFeesPerPlatform').value }">
                  Set flat fees per platform
                </div>
              </mat-checkbox>
            </div>
          </div>

          <ng-container formGroupName="flat_fees">
            <div
              *ngIf="form.get('ui.flatFeesPerPlatform').value"
              class="grid grid-cols-[repeat(auto-fit,minmax(88px,1fr))] gap-4">
              <div class="w-[5rem] flex flex-col gap-1" *ngFor="let field of perPlatformOptions">
                <label style="font-size: 0.75rem">{{ field.label }}</label>
                <mat-form-field class="input__mini nopadding zeroSuffix w-[75px]">
                  <input matInput type="number" placeholder="0" min="0" [formControlName]="field.value" />
                </mat-form-field>
              </div>
            </div>
          </ng-container>
        </div>
      </ng-container>
    </div>
  </div>

  <h3 class="mb-2 mt-9">Who is responsible for remitting pass-through taxes?</h3>
  <p>
    Pass-through taxes are taxes that are collected with the reservation payment, and passed through in the payout.
    These pass-through taxes must ultimately be remitted.
  </p>
  <div class="flex flex-col md:flex-row gap-6 mb-8 items-stretch">
    <sbnb-radio-card
      [disabled]="!form.disabled && form.get('taxes').disabled"
      [readonly]="form.disabled"
      context="neutral"
      *ngFor="let option of taxOptions"
      [selected]="form.get('taxes').value === option.value"
      (click)="form.get('taxes').setValue(option.value)">
      <h4 class="mb-2">{{ option.title }}</h4>
      <p>
        {{ option.subtitle }}
      </p>
    </sbnb-radio-card>
  </div>

  <ng-container *ngIf="showProperties">
    <h3 class="mt-6">Properties</h3>
    <p>Select all the properties that use this agreement.</p>

    <ng-container *ngIf="propertiesState$ | async as propertiesState">
      <sbnb-statements-property-picker
        [properties]="propertiesState.data"
        [disabledProperties]="propertiesWithCommission$ | async"
        (selectedPropertiesChange)="setProperties($event)"></sbnb-statements-property-picker>
    </ng-container>
  </ng-container>
</form>
